import {ProcedureType, StudyType} from 'graphql-client/__generated/graphqlGlobalTypes';
import notifications, {Response} from 'notifications';
import {logProfilerTimingEvent} from 'performance/BenchlingProfiler';
import {
  STUDIES_ADD_CONDITIONS_STARTED_EVENT,
  STUDIES_ADD_CONDITIONS_ENDED_EVENT,
  composeStudiesAddConditionsEventMetadata,
  ExecutionType,
} from 'performance/processStudiesEvents';
import {
  getNewConditionCount,
  getNewConditionReplicateCount,
  updateProcedureRun,
  UpdateProcedureRunParams,
} from 'procedures/network/ProcedureRunDAO';
import {getProcedureRunConditionTextForProcedureType} from 'studies/TypedStudyUtils';
import * as net from 'util/Net';
import {withLoadingScreen} from 'util/Promises';

import {
  getConditionCreationUnknownErrorAction,
  showConditionCreationTrayTask,
} from './ConditionCreationTrayTask';

interface ProcedureRunUpdateCallbacks {
  onSuccess: () => void;
  onOpen: () => void;
  /** Called when the save fails, so the caller can leave any pending "saving" state. */
  onError: () => void;
}

/**
 * Handles a synchronous save rejection (async failures surface through the tray task instead). Resets
 * the caller via onError and shows an error toast, returning null so callers can stop. Rethrows
 * non-server errors so real bugs still surface.
 */
function handleSyncSaveError(error: unknown, onError: () => void): null {
  onError();
  if (!net.isServerRequestError(error)) {
    throw error;
  }
  notifications.notifyError(error as Response);
  return null;
}

export async function onAddConditions(
  studyId: string,
  procedureType: ProcedureType,
  studyType: StudyType,
  update: UpdateProcedureRunParams,
  callbacks: ProcedureRunUpdateCallbacks
): Promise<void> {
  const numNewConditions = getNewConditionCount(update);
  logProfilerTimingEvent(STUDIES_ADD_CONDITIONS_STARTED_EVENT);
  const onUnknownError = getConditionCreationUnknownErrorAction(
    studyId,
    studyType,
    update.id,
    procedureType,
    numNewConditions,
    callbacks.onError
  );
  const initResponse = await withLoadingScreen(
    updateProcedureRun(update, {supportsAsync: true}),
    `Adding ${getProcedureRunConditionTextForProcedureType(procedureType, {
      plural: numNewConditions > 1,
    })}...`
  ).catch((error: unknown) => handleSyncSaveError(error, callbacks.onError));
  if (initResponse == null) {
    return;
  }
  if (!initResponse.async) {
    callbacks.onSuccess();
    callbacks.onOpen();
    logProfilerTimingEvent(
      STUDIES_ADD_CONDITIONS_ENDED_EVENT,
      composeStudiesAddConditionsEventMetadata(studyType, ExecutionType.SYNC)
    );
    return;
  }

  showConditionCreationTrayTask(
    initResponse.data.taskId,
    initResponse.data.status_url,
    initResponse.taskMeta,
    [
      {
        id: update.id,
        studyId,
        procedureType,
        studyType,
        numNewConditions,
      },
    ],
    {
      onSuccess: (response) => {
        callbacks.onSuccess();
        logProfilerTimingEvent(
          STUDIES_ADD_CONDITIONS_ENDED_EVENT,
          composeStudiesAddConditionsEventMetadata(studyType, ExecutionType.ASYNC)
        );
      },
      onOpen: callbacks.onOpen,
      onUnknownError,
    },
    initResponse.data.timingMeta
  );
}

/**
 * General procedure run update handler used by the split-pool design flow. Unlike onAddConditions, it
 * keeps non-condition updates (e.g. drawn edges) synchronous, since the backend only runs async tasks
 * for condition creation. Will grow to cover deleting conditions and edges.
 */
export async function onUpdateProcedureRun(
  studyId: string,
  procedureType: ProcedureType,
  studyType: StudyType,
  // The number of active + downstream unit operations
  numCoveredUnitOperations: number,
  update: UpdateProcedureRunParams,
  callbacks: ProcedureRunUpdateCallbacks
): Promise<void> {
  // Only condition creation runs async, so edge-only updates stay synchronous. The shared helper
  // counts new conditions across both condition fields.
  const numNewConditions = getNewConditionCount(update);
  const hasNewConditions = numNewConditions > 0;
  const onUnknownError = getConditionCreationUnknownErrorAction(
    studyId,
    studyType,
    update.id,
    procedureType,
    numNewConditions,
    callbacks.onError
  );
  const initResponse = await withLoadingScreen(
    updateProcedureRun(update, {supportsAsync: hasNewConditions}),
    'Saving changes...'
  ).catch((error: unknown) => handleSyncSaveError(error, callbacks.onError));
  if (initResponse == null) {
    return;
  }
  if (!initResponse.async) {
    callbacks.onSuccess();
    callbacks.onOpen();
    return;
  }

  showConditionCreationTrayTask(
    initResponse.data.taskId,
    initResponse.data.status_url,
    initResponse.taskMeta,
    [
      {
        id: update.id,
        studyId,
        procedureType,
        studyType,
        numNewConditions,
        numNewConditionReplicates: getNewConditionReplicateCount(update),
        numCoveredUnitOperations,
      },
    ],
    {
      onSuccess: () => {
        callbacks.onSuccess();
      },
      onOpen: callbacks.onOpen,
      onUnknownError,
    },
    initResponse.data.timingMeta
  );
}
